'use client';

import { useRef, useState } from 'react';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShoppingBag, User, X } from 'lucide-react';
import { useCheckoutAuth } from '../hooks/use-checkout-auth';
import { SignInButton, SignUpButton, useAuth } from '@clerk/nextjs';

interface CheckoutAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CheckoutAuthModal({ isOpen, onClose }: CheckoutAuthModalProps) {
  const [guestEmail, setGuestEmail] = useState('');
  
  const { isSignedIn } = useAuth();
  const { isLoading, handleGuestCheckout, proceedToCheckout, setGuestEmail: setCheckoutGuestEmail } = useCheckoutAuth();
  const didFinalizeRef = useRef(false);

  const handleGuestCheckoutClick = async () => {
    setCheckoutGuestEmail(guestEmail);
    await handleGuestCheckout();
    onClose();
  };

  // If the user signs in via Clerk modal while this dialog is open,
  // simply close the modal and keep them on the cart. Do not create an order yet.
  useEffect(() => {
    if (isOpen && isSignedIn && !didFinalizeRef.current) {
      didFinalizeRef.current = true;
      onClose();
    }
  }, [isOpen, isSignedIn, onClose]);

  // Reset the guard when the modal is closed
  useEffect(() => {
    if (!isOpen) {
      didFinalizeRef.current = false;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Complete Your Purchase</h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-8">
            {/* Guest Checkout */}
            <Card className="p-8">
              <div className="space-y-6">
                <div className="text-center space-y-2">
                  <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <ShoppingBag className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold">Checkout as Guest</h3>
                  <p className="text-muted-foreground">
                    Continue with your purchase without creating an account
                  </p>
                </div>
                
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    Fast and simple checkout
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    No account required
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    Secure payment processing
                  </li>
                </ul>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="guest-email">Email (for order updates)</Label>
                    <Input
                      id="guest-email"
                      type="email"
                      placeholder="Enter your email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      required
                    />
                  </div>

                  <Button 
                    onClick={handleGuestCheckoutClick} 
                    size="lg" 
                    className="w-full"
                    disabled={!guestEmail || isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Continue as Guest'}
                  </Button>
                </div>
              </div>
            </Card>

            {/* Sign In / Sign Up with Clerk */}
            <Card className="p-8">
              <div className="space-y-6">
                <div className="text-center space-y-2">
                  <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold">Sign In</h3>
                  <p className="text-muted-foreground">
                    Access your Flashpoint Collectibles account
                  </p>
                </div>

                <div className="space-y-4">
                  <SignInButton mode="modal">
                    <Button size="lg" className="w-full" disabled={isLoading}>
                      {isLoading ? 'Signing In...' : 'Sign In'}
                    </Button>
                  </SignInButton>
                </div>

                <div className="text-center">
                  <Separator className="my-4" />
                  <p className="text-sm text-muted-foreground">
                    Don't have an account?{' '}
                    <SignUpButton mode="modal">
                      <Button 
                        variant="link" 
                        className="p-0 h-auto font-medium"
                      >
                        Create one here
                      </Button>
                    </SignUpButton>
                  </p>
                </div>

                <div className="space-y-2 text-sm text-muted-foreground">
                  <p className="font-medium">Account benefits:</p>
                  <ul className="space-y-1">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                      Order history and tracking
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                      Saved addresses and payment methods
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                      Exclusive member offers
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
